import type { GuideId } from './guides';
import { COPY } from './copy';

/**
 * One line of the server-side checklist. `unknown` when this user cannot see the answer. `guide`
 * is the public guide that explains the step, linked while the step is not done.
 */
export type SetupStep = { id: string; state: 'done' | 'todo' | 'unknown'; text: string; guide: GuideId };

type StepCopy = { done: string; todo: string; unknown?: string };

type StepParams = { id: string; state: SetupStep['state']; copy: StepCopy; guide: GuideId };

const step = ({ id, state, copy, guide }: StepParams): SetupStep => ({
  id,
  state,
  text: state === 'unknown' ? (copy.unknown ?? copy.todo) : copy[state],
  guide,
});

type SetupStepsParams = {
  visitorIdEnabled: boolean;
  /** Active server keys on the website, or null when this user cannot read them (or they are loading). */
  activeKeys: number | null;
  flagKey: string | null | undefined;
  variants: readonly { key?: string | null }[];
};

/**
 * What a server-side experiment needs before it can start, in order, from what the dashboard
 * already knows. The API refuses a start without the visitor ID, the flag key or a variant
 * key; without a server key the backend simply cannot ask.
 */
export function setupSteps({ visitorIdEnabled, activeKeys, flagKey, variants }: SetupStepsParams): SetupStep[] {
  const keyState: SetupStep['state'] = activeKeys === null ? 'unknown' : activeKeys > 0 ? 'done' : 'todo';
  const variantKeysSet = variants.length > 0 && variants.every((variant) => !!variant.key);
  return [
    step({
      id: 'visitor-id',
      state: visitorIdEnabled ? 'done' : 'todo',
      copy: COPY.setup.visitorId,
      guide: 'visitorId',
    }),
    step({ id: 'server-key', state: keyState, copy: COPY.setup.serverKey, guide: 'serverKeys' }),
    step({ id: 'flag-key', state: flagKey ? 'done' : 'todo', copy: COPY.setup.flagKey, guide: 'serverSetup' }),
    step({
      id: 'variant-keys',
      state: variantKeysSet ? 'done' : 'todo',
      copy: COPY.setup.variantKeys,
      guide: 'serverSetup',
    }),
  ];
}
